// src/pages/Users/Inscription/hooks/useInscriptionForm.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getBeltsList } from "../../../../services/beltServices";
import {
  addNewAdherent,
  getAllAdherentsList,
} from "../../../../services/adherentServices";
import { assignAdherentToUser } from "../../../../services/userServices";
import { createNewDossierInscription } from "../../../../services/dossierServices";
import {
  sendNotification,
  sendNotificationToAdmin,
} from "../../../../services/notificationServices";
import { getSettings } from "../../../../services/settingsServices";
import { getGroupsList } from "../../../../services/groupServices";
import { getDojos } from "../../../../services/dojoServices";
import { useUser } from "../../../Home/UserContext";

import {
  buildDojosById,
  buildInscriptionPayload,
  canRegisterToGroup,
  DEFAULT_FORM_DATA,
  formatBirthDateInput,
  formatPrenom,
  getGroupUnavailableReason,
  getGroupWarning,
  isValidBirthDateInput,
  toBool,
  validateInscriptionBeforeSubmit,
} from "../utils/inscriptionUtils";

const APP_URL = (process.env.REACT_APP_URL || window.location.origin).replace(
  /\/$/,
  "",
);

export function useInscriptionForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  const groupeIdFromState = location.state?.groupeId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [belts, setBelts] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [adherentsList, setAdherentsList] = useState([]);

  const [saisonActive, setSaisonActive] = useState("");
  const [isQuotaActive, setIsQuotaActive] = useState(false);

  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [userInfo, setUserInfo] = useState({
    nom: "",
    prenom: "",
    userId: "",
  });

  const [dateError, setDateError] = useState("");
  const [groupError, setGroupError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState("");

  const [communes, setCommunes] = useState([]);
  const [communeLoading, setCommuneLoading] = useState(false);

  const dojosById = useMemo(() => buildDojosById(dojos), [dojos]);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);

      const storedUserInfo = JSON.parse(localStorage.getItem("userInfo"));

      if (storedUserInfo) {
        setUserInfo({
          nom: storedUserInfo.nom || "",
          prenom: storedUserInfo.prenom || "",
          userId: storedUserInfo.userId || "",
        });
      }

      const [settingsData, groupsData, dojosData, beltsData, adherentsData] =
        await Promise.all([
          getSettings(),
          getGroupsList(),
          getDojos(),
          getBeltsList(),
          getAllAdherentsList(),
        ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;
      const activeSeason = settings?.saison_active || "";

      setSaisonActive(activeSeason);
      setIsQuotaActive(toBool(settings?.isquotaactive));

      setFormData((prev) => ({
        ...prev,
        saison_active: activeSeason,
      }));

      const activeGroups = Array.isArray(groupsData)
        ? groupsData.filter((group) => toBool(group?.is_active))
        : [];

      setGroups(activeGroups);
      setDojos(Array.isArray(dojosData) ? dojosData : []);
      setBelts(Array.isArray(beltsData) ? beltsData : []);
      setAdherentsList(Array.isArray(adherentsData) ? adherentsData : []);

      if (groupeIdFromState) {
        const preselected = activeGroups.find(
          (group) => String(group.id) === String(groupeIdFromState),
        );

        if (preselected) {
          const unavailableReason = getGroupUnavailableReason(
            preselected,
            toBool(settings?.isquotaactive),
          );

          if (unavailableReason) {
            setGroupError(`${preselected.nom} : ${unavailableReason}.`);
          } else {
            setSelectedDojo(
              preselected.dojo_id ? String(preselected.dojo_id) : "",
            );
            setSelectedGroups([preselected]);
          }
        }
      }
    } catch (error) {
      console.error("Erreur chargement inscription :", error);
      toast.error("Impossible de charger les informations d’inscription.");
    } finally {
      setLoading(false);
    }
  }, [groupeIdFromState]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const cp = formData.cp;

    if (!/^\d{5}$/.test(cp)) {
      setCommunes([]);
      setCommuneLoading(false);
      return;
    }

    let aborted = false;

    setCommuneLoading(true);

    fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&format=json`,
    )
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (aborted) return;

        const list = Array.isArray(data)
          ? data
              .map((commune) => (commune?.nom || "").toUpperCase())
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "fr"))
          : [];

        setCommunes(list);

        if (list.length === 1) {
          setFormData((prev) => ({
            ...prev,
            commune: list[0],
          }));
        }
      })
      .catch(() => {
        if (!aborted) setCommunes([]);
      })
      .finally(() => {
        if (!aborted) setCommuneLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [formData.cp]);

  const checkForDuplicate = useCallback(
    (nom, prenom) => {
      const duplicate = adherentsList.find(
        (adherent) =>
          String(adherent.nom || "").toUpperCase() ===
            String(nom || "").toUpperCase() &&
          String(adherent.prenom || "").toLowerCase() ===
            String(prenom || "").toLowerCase(),
      );

      if (duplicate) {
        setDuplicateWarning(
          `Un adhérent avec le nom ${nom} et le prénom ${prenom} existe déjà.`,
        );
      } else {
        setDuplicateWarning("");
      }
    },
    [adherentsList],
  );

  const handleChange = useCallback(
    (event) => {
      const { name, value, type, checked } = event.target;

      if (type === "checkbox") {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
        return;
      }

      let formattedValue = value;

      if (name === "tel" || name === "tel_secondaire") {
        formattedValue = value.replace(/\D/g, "").slice(0, 15);
      } else if (name === "nom") {
        formattedValue = value.toUpperCase().replace(/[^A-ZÀ-Ÿ\s-]/g, "");
      } else if (name === "prenom") {
        formattedValue = formatPrenom(value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, ""));
      } else if (name === "poids") {
        formattedValue = value.replace(/[^0-9.]/g, "");
      } else if (name === "commune") {
        formattedValue = value.toUpperCase();
      } else if (name === "cp") {
        formattedValue = value.replace(/\D/g, "").slice(0, 5);
        setCommunes([]);
        setFormData((prev) => ({
          ...prev,
          cp: formattedValue,
          commune: "",
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));

      if (name === "nom" || name === "prenom") {
        checkForDuplicate(
          name === "nom" ? formattedValue : formData.nom,
          name === "prenom" ? formattedValue : formData.prenom,
        );
      }
    },
    [checkForDuplicate, formData.nom, formData.prenom],
  );

  const handleDateChange = useCallback((event) => {
    const formattedValue = formatBirthDateInput(event.target.value);

    setFormData((prev) => ({
      ...prev,
      date_naissance: formattedValue,
    }));

    if (
      formattedValue.length === 10 &&
      !isValidBirthDateInput(formattedValue)
    ) {
      setDateError(
        "La date n’est pas valide ou est une date future. Veuillez entrer une date au format JJ/MM/AAAA.",
      );
    } else {
      setDateError("");
    }
  }, []);

  const setInscriptionType = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      statut_inscription: value,
    }));
  }, []);

  const displayedGroups = useMemo(() => {
    const selectedIds = new Set(selectedGroups.map((group) => group.id));

    return groups
      .filter((group) => !selectedIds.has(group.id))
      .filter((group) => {
        if (!selectedDojo) return true;
        return Number(group.dojo_id) === Number(selectedDojo);
      })
      .sort((a, b) => {
        const orderA = Number(a.ordre_groupe || 0);
        const orderB = Number(b.ordre_groupe || 0);

        if (orderA !== orderB) return orderA - orderB;

        return String(a.nom || "").localeCompare(String(b.nom || ""), "fr");
      });
  }, [groups, selectedDojo, selectedGroups]);

  const addGroup = useCallback(
    (group) => {
      if (!group?.id) return;

      const unavailableReason = getGroupUnavailableReason(group, isQuotaActive);

      if (unavailableReason) {
        setGroupError(`${group.nom} : ${unavailableReason}.`);
        return;
      }

      const warning = getGroupWarning(group, isQuotaActive);

      setSelectedGroups((prev) => [...prev, group]);
      setGroupError(warning);
    },
    [isQuotaActive],
  );

  const removeGroup = useCallback((groupId) => {
    setSelectedGroups((prev) => prev.filter((group) => group.id !== groupId));
    setGroupError("");
  }, []);

  const canSubmit = useMemo(() => {
    return (
      !submitting &&
      !!formData.statut_inscription &&
      !!formData.nom &&
      !!formData.prenom &&
      !!formData.genre &&
      !!formData.date_naissance &&
      !!formData.lieu_naissance &&
      !!formData.adresse &&
      !!formData.cp &&
      !!formData.commune &&
      !!formData.tel &&
      !!formData.email &&
      !!formData.accept_reglement &&
      !!formData.accept_infos_exactes &&
      !!formData.accept_responsabilite &&
      selectedGroups.length > 0 &&
      !dateError
    );
  }, [dateError, formData, selectedGroups.length, submitting]);

  const totalAmount = useMemo(() => {
    return selectedGroups.reduce((sum, group) => {
      return (
        sum +
        Number(group.tarif_licence || 0) +
        Number(group.tarif_cotisation || 0)
      );
    }, 0);
  }, [selectedGroups]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateInscriptionBeforeSubmit({
      formData,
      selectedGroups,
      dateError,
      user,
      isQuotaActive,
    });

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSubmitting(true);

      const adherentData = buildInscriptionPayload({
        formData,
        selectedGroups,
        saisonActive,
      });

      const addResponse = await addNewAdherent(adherentData);

      if (!addResponse?.id) {
        toast.error(
          "Erreur lors de l’ajout de l’adhérent. Veuillez réessayer.",
        );
        return;
      }

      await assignAdherentToUser(user.userId, addResponse.id, user.token);
      await createNewDossierInscription(addResponse.id);

      const groupNames = selectedGroups.map((group) => group.nom).join(", ");

      sendNotification(
        `Félicitations, vous venez d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`,
        user.userId,
        user.token,
      );

      sendNotificationToAdmin(
        `${userInfo.prenom} ${userInfo.nom} vient d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`,
        user.token,
      );

      toast.success("Adhérent ajouté avec succès.");

      window.location.href = `${APP_URL}/dossier-d-inscription`;
    } catch (error) {
      console.error("Erreur inscription :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’ajout de l’adhérent. Veuillez réessayer.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    loading,
    submitting,
    user,
    userInfo,

    belts,
    dojos,
    dojosById,
    groups,
    displayedGroups,
    selectedDojo,
    setSelectedDojo,
    selectedGroups,
    addGroup,
    removeGroup,

    saisonActive,
    isQuotaActive,

    formData,
    setFormData,
    handleChange,
    handleDateChange,
    setInscriptionType,

    dateError,
    groupError,
    duplicateWarning,

    communes,
    communeLoading,

    canSubmit,
    totalAmount,
    handleSubmit,

    canRegisterToGroup: (group) => canRegisterToGroup(group, isQuotaActive),
    getGroupUnavailableReason: (group) =>
      getGroupUnavailableReason(group, isQuotaActive),
  };
}
